import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const publicContact = {
  email: process.env.REACT_APP_CONTACT_EMAIL?.trim(),
  phone: process.env.REACT_APP_CONTACT_PHONE?.trim(),
  location: process.env.REACT_APP_CONTACT_LOCATION?.trim(),
};

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", website: "" });
  const [loading, setLoading] = useState(false);

  // The form now calls the persisted backend workflow. Success is shown only
  // after the request is stored; the hidden website field is a bot honeypot.
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill all required fields");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact-messages", form);
      toast.success("Message received. Our team will review it shortly.");
      setForm({ name: "", email: "", subject: "", message: "", website: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="premium-page-surface max-w-5xl mx-auto px-4 py-12">
      <h1 className="text-3xl font-bold mb-2">Contact Us</h1>
      <p className="text-muted-foreground mb-8">
        Have a question or need assistance? We're here to help.
      </p>

      <div className="grid md:grid-cols-2 gap-10">
        <div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="hidden" aria-hidden="true">
              <Label htmlFor="contact-website">Website</Label>
              <Input
                id="contact-website"
                value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
                autoComplete="off"
                tabIndex={-1}
              />
            </div>
            <div>
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Your full name"
                autoComplete="name"
                maxLength={120}
                required
              />
            </div>
            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="your@email.com"
                autoComplete="email"
                maxLength={254}
                required
              />
            </div>
            <div>
              <Label htmlFor="subject">Subject</Label>
              <Input
                id="subject"
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                placeholder="What's this about?"
                maxLength={160}
              />
            </div>
            <div>
              <Label htmlFor="message">Message *</Label>
              <textarea
                id="message"
                rows={5}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Describe your query..."
                minLength={10}
                maxLength={4000}
                required
              />
            </div>
            <Button type="submit" disabled={loading} className="w-full">
              <Send className="h-4 w-4 mr-2" />
              {loading ? "Sending..." : "Send Message"}
            </Button>
          </form>
        </div>

        <div className="space-y-6">
          {publicContact.email && <div className="premium-card premium-card-interactive p-6 border rounded-2xl hover:-translate-y-0.5 transition-all duration-200 group">
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 ring-1 ring-primary/10 text-primary mb-2 shadow-sm group-hover:scale-110 group-hover:-rotate-3 transition-all duration-300 animate-gradient-pan"><Mail className="h-5 w-5 group-hover:animate-icon-bob" /></span>
            <h3 className="font-semibold mb-1">Email</h3>
            <a href={`mailto:${publicContact.email}`} className="text-muted-foreground break-all hover:text-primary hover:underline">{publicContact.email}</a>
          </div>}
          {publicContact.phone && <div className="premium-card premium-card-interactive p-6 border rounded-2xl hover:-translate-y-0.5 transition-all duration-200 group">
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 ring-1 ring-primary/10 text-primary mb-2 shadow-sm group-hover:scale-110 group-hover:-rotate-3 transition-all duration-300 animate-gradient-pan"><Phone className="h-5 w-5 group-hover:animate-icon-bob" /></span>
            <h3 className="font-semibold mb-1">Phone</h3>
            <a href={`tel:${publicContact.phone.replace(/[^+\d]/g, "")}`} className="text-muted-foreground hover:text-primary hover:underline">{publicContact.phone}</a>
          </div>}
          {publicContact.location && <div className="premium-card premium-card-interactive p-6 border rounded-2xl hover:-translate-y-0.5 transition-all duration-200 group">
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 ring-1 ring-primary/10 text-primary mb-2 shadow-sm group-hover:scale-110 group-hover:-rotate-3 transition-all duration-300 animate-gradient-pan"><MapPin className="h-5 w-5 group-hover:animate-icon-bob" /></span>
            <h3 className="font-semibold mb-1">Office</h3>
            <p className="text-muted-foreground whitespace-pre-line">{publicContact.location}</p>
          </div>}
          {!publicContact.email && !publicContact.phone && !publicContact.location && (
            <div className="premium-card p-6 border rounded-2xl" role="note">
              <Mail className="h-6 w-6 text-primary mb-3" />
              <h3 className="font-semibold mb-1">Send us a message</h3>
              <p className="text-sm text-muted-foreground">Direct contact details have not been published. Use the secure form and the team will review your request.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
